import { z } from "zod";
import { useTRPC } from "../../trpc";
import { zod4Resolver } from "mantine-form-zod-resolver";
import { useCallback, useMemo } from "react";
import { RelativeLink } from "../../components/Link";
import { Alert, Button, Loader, PasswordInput, Stack, Text, Title } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useAllParams } from "../../hooks/router";
import { FormattedMessage, useIntl } from "react-intl";
import { passwordMismatch, passwordSchemaStrength } from "../../utils/schema";
import { PasswordStrength } from "../../components/PasswordStrength";
import { captureEvent } from "../../utils/posthog";
import { useSetUser } from "../../hooks/user";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Event } from "../../constants/events";
import { useNavigateUserAuth } from "./hooks/navigate";
import { IconLock } from "@tabler/icons-react";

const initialValues = {
  password: "",
  passwordConfirmation: "",
};

export function ChangePassword() {
  const intl = useIntl();
  const trpc = useTRPC();
  const navigate = useNavigateUserAuth();
  const setUser = useSetUser();
  const { token = "" } = useAllParams();
  const { data: isExpired, isFetching } = useQuery(trpc.checkIfExpired.queryOptions({ token }));

  const { mutate, isPending } = useMutation(trpc.resetPassword.mutationOptions({
    onSuccess: (user) => {
      setUser(user);
      navigate(user);
    },
  }));

  const validate = useMemo(() => zod4Resolver(
    z.object({
      password: passwordSchemaStrength(intl),
      passwordConfirmation: passwordSchemaStrength(intl),
    }).refine(({ password, passwordConfirmation }) => password === passwordConfirmation, {
      message: passwordMismatch(intl),
      path: ["passwordConfirmation"],
    }),
  ), [intl]);

  const { onSubmit, getInputProps, isValid, values } = useForm({
    validateInputOnBlur: true,
    initialValues,
    validate,
  });

  const formValid = isValid();

  const handleSubmit = useCallback((form: typeof initialValues) => {
    mutate({
      token,
      ...form,
    });
    captureEvent(Event.AuthChangedPassword);
  }, [mutate, token]);

  return (
    <>
      {isFetching && (
        <Stack gap={16}>
          <Loader size="lg" />
          <Text size="xl">
            <FormattedMessage id="text.verifyingRequest" defaultMessage="Verifying request" />
          </Text>
        </Stack>
      )}
      {isExpired && (
        <Alert variant="light" color="red">
          <Stack gap={16}>
            <Title size="h3">
              <FormattedMessage id="title.invalidRequest" defaultMessage="Invalid request" />
            </Title>
            <Text>
              <FormattedMessage id="text.passwordResetExpiredOrInvalid" defaultMessage="Link expired or is invalid. Please create new password request using link below." />
            </Text>
            <RelativeLink underline="always" href="../../reset-password">
              <FormattedMessage id="link.requestResetPassword" defaultMessage="Request reset password" />
            </RelativeLink>
          </Stack>
        </Alert>
      )}
      {!isExpired && (
        <Stack>
          <form onSubmit={onSubmit(handleSubmit)}>
            <Stack gap="1rem">
              <Title size="h3">
                <FormattedMessage id="title.changePassword" defaultMessage="Change password" />
              </Title>
              <Text>
                <FormattedMessage id="label.passwordRequirements" defaultMessage="Your password must be at least ten characters and include special characters." />
              </Text>
              <PasswordInput
                leftSection={<IconLock size={16} />}
                label={<FormattedMessage id="label.password" defaultMessage="Password" />}
                autoComplete="new-password"
                {...getInputProps("password")}
              />
              <PasswordInput
                leftSection={<IconLock size={16} />}
                label={<FormattedMessage id="label.passwordConfirmation" defaultMessage="Password confirmation" />}
                autoComplete="new-password"
                {...getInputProps("passwordConfirmation")}
              />
              <PasswordStrength {...values} />
              <Stack gap="1rem">
                <Button
                  type="submit"
                  fullWidth
                  loading={isPending}
                  disabled={!formValid}
                  variant="primary"
                >
                  <FormattedMessage id="button.updatePassword" defaultMessage="Update password" />
                </Button>

                <RelativeLink underline="always" href="../sign-in">
                  <FormattedMessage id="link.returnToSignIn" defaultMessage="Return to sign in" />
                </RelativeLink>
              </Stack>
            </Stack>
          </form>
        </Stack>
      )}
    </>
  );
}
